import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/AppShell";
import { ADMIN_NAV } from "./_nav";
import { api, errFrom } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import MultiImageCard from "../../components/MultiImageCard";
import { toast } from "sonner";
import { Filter, Calendar, Building2 } from "lucide-react";

export default function AdminTags() {
  const [items, setItems] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [f, setF] = useState({ dealership_id: "", start: "", end: "", month: "", year: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/dealerships").then(({ data }) => setDealerships(data)); }, []);

  async function load() {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data } = await api.get("/admin/dealership-tags", { params });
      setItems(data);
    } catch (e) { toast.error(errFrom(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 1, y, y + 1].map(String);
  }, []);

  return (
    <AppShell nav={ADMIN_NAV} title="Dealership Tags" subtitle="Gallery">
      <div className="brand-surface rounded-2xl p-4 md:p-5 mb-6" data-testid="tags-filters">
        <div className="flex items-center gap-2 mb-4 text-overline"><Filter size={12} />Filters</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase text-zinc-500">Dealership</Label>
            <Select value={f.dealership_id || "__all"} onValueChange={(v) => setF({ ...f, dealership_id: v === "__all" ? "" : v })}>
              <SelectTrigger className="h-10 bg-[#0A0A0A] border-white/10 mt-1" data-testid="filter-dealership"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10 text-white">
                <SelectItem value="__all">All dealerships</SelectItem>
                {dealerships.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px] uppercase text-zinc-500">Start</Label><Input type="date" className="h-10 bg-[#0A0A0A] border-white/10 mt-1" value={f.start ? f.start.substring(0, 10) : ""} onChange={(e) => setF({ ...f, start: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="filter-start" /></div>
          <div><Label className="text-[10px] uppercase text-zinc-500">End</Label><Input type="date" className="h-10 bg-[#0A0A0A] border-white/10 mt-1" value={f.end ? f.end.substring(0, 10) : ""} onChange={(e) => setF({ ...f, end: e.target.value ? new Date(e.target.value + "T23:59:59").toISOString() : "" })} data-testid="filter-end" /></div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Month</Label>
            <Select value={f.month || "__any"} onValueChange={(v) => setF({ ...f, month: v === "__any" ? "" : v })}>
              <SelectTrigger className="h-10 bg-[#0A0A0A] border-white/10 mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10 text-white">
                <SelectItem value="__any">Any</SelectItem>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <SelectItem key={m} value={String(m)}>{new Date(2000, m - 1).toLocaleString(undefined, { month: "long" })}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-500">Year</Label>
            <Select value={f.year || "__any"} onValueChange={(v) => setF({ ...f, year: v === "__any" ? "" : v })}>
              <SelectTrigger className="h-10 bg-[#0A0A0A] border-white/10 mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10 text-white">
                <SelectItem value="__any">Any</SelectItem>
                {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button onClick={load} className="bg-[#FF3B30] hover:bg-[#E02920] h-10" data-testid="apply-filters">Apply</Button>
          <Button variant="ghost" className="text-zinc-400" onClick={() => { setF({ dealership_id: "", start: "", end: "", month: "", year: "" }); setTimeout(load, 0); }} data-testid="clear-filters">Clear</Button>
          <div className="ml-auto text-xs text-zinc-500 self-center">{items.length} results</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="brand-surface rounded-2xl p-12 text-center text-zinc-500">No tags match the filters.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((t) => (
            <MultiImageCard key={t.id} item={t} testId={`tag-card-${t.id}`}>
              <div className="flex items-center gap-1.5 text-xs text-[#FF3B30] font-semibold truncate"><Building2 size={12} />{t.dealership_name}</div>
              <div className="text-sm font-semibold truncate mt-1">by {t.user_name}</div>
              <div className="flex items-center gap-1 text-[11px] text-zinc-500 mt-1"><Calendar size={10} />{new Date(t.created_at).toLocaleString()}</div>
            </MultiImageCard>
          ))}
        </div>
      )}
    </AppShell>
  );
}
